/**
 * Iter269.20z — Heading anchor copy-link.
 *
 * Attaches a GitHub-style "#" hover icon to every `<h2 id="...">` and
 * `<h3 id="...">` heading inside `.qt-doc-content`. Clicking the icon
 * copies the section's permalink to the clipboard and flashes a tiny
 * "Copied!" pill. Hidden by default; reveals on heading hover.
 *
 * Why this matters for QorTrace:
 *   - Auditors / compliance teams routinely want to forward "section
 *     §8.2 of your terms" to colleagues; they need a shareable URL.
 *   - Pairs with the existing in-page TOC (Methodology, Audit, Legal
 *     pages) and the new <ScrollToTop> reset behavior so deep links
 *     land cleanly at the right scroll position.
 *
 * Implementation:
 *   - One global mount inside <QorTraceOnlyChrome>.
 *   - Decorates ALL headings inside any `.qt-doc-content` container
 *     OR inside `.qt-legal-prose` (used by LegalLayout) that have an
 *     id. Idempotent — safe to re-run on every route change.
 *   - Listens to MutationObserver so late-mounting prose still
 *     gets decorated.
 *   - Pure DOM (no per-page wiring needed).
 */
import React, { useEffect, useRef } from "react";

const TARGET_SELECTOR = ".qt-doc-content, .qt-legal-prose";
const HEADING_SELECTOR = "h2[id], h3[id]";
const DECORATED_ATTR  = "data-anchor-link";

function buildAnchorIcon(id) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "qt-heading-anchor";
  btn.setAttribute("data-anchor-for", id);
  btn.setAttribute("aria-label", `Copy link to ${id}`);
  btn.setAttribute("title", "Copy link to this section");
  btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round"
    stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`;
  return btn;
}

function showCopiedToast() {
  const old = document.getElementById("qt-anchor-toast");
  if (old) old.remove();
  const t = document.createElement("div");
  t.id = "qt-anchor-toast";
  t.textContent = "Link copied";
  t.style.cssText = `
    position:fixed; left:50%; bottom:32px; transform:translateX(-50%);
    background:rgba(11,19,36,0.95); color:#00E5FF;
    border:1px solid rgba(0,229,255,0.45);
    padding:8px 16px; border-radius:6px;
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
    font-size:11px; letter-spacing:0.16em; text-transform:uppercase;
    box-shadow:0 4px 18px rgba(0,229,255,0.18);
    z-index:9999; opacity:0; transition:opacity 180ms ease;
    backdrop-filter: blur(8px);
  `;
  document.body.appendChild(t);
  requestAnimationFrame(() => { t.style.opacity = "1"; });
  setTimeout(() => {
    t.style.opacity = "0";
    setTimeout(() => t.remove(), 220);
  }, 1600);
}

export default function HeadingAnchorLinks() {
  const observerRef = useRef(null);

  useEffect(() => {
    const decorate = () => {
      const containers = document.querySelectorAll(TARGET_SELECTOR);
      containers.forEach((container) => {
        container.querySelectorAll(HEADING_SELECTOR).forEach((h) => {
          if (h.getAttribute(DECORATED_ATTR) === "true") return;
          h.setAttribute(DECORATED_ATTR, "true");
          // Make the heading position-relative so the absolute-positioned
          // icon can sit beside it.
          const cs = getComputedStyle(h);
          if (cs.position === "static") h.style.position = "relative";
          const btn = buildAnchorIcon(h.id);
          h.appendChild(btn);
          btn.addEventListener("click", (ev) => {
            ev.preventDefault();
            const url = `${window.location.origin}${window.location.pathname}#${h.id}`;
            try {
              navigator.clipboard.writeText(url).then(() => {
                if (window.history && window.history.replaceState) {
                  window.history.replaceState(null, "", `#${h.id}`);
                }
                showCopiedToast();
              });
            } catch {
              showCopiedToast();
            }
          });
        });
      });
    };

    decorate();
    // Re-decorate on route changes + late-mounting prose.
    const mo = new MutationObserver(() => decorate());
    mo.observe(document.body, { childList: true, subtree: true });
    observerRef.current = mo;
    const t = setTimeout(decorate, 700);

    return () => {
      clearTimeout(t);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return (
    <style>{`
      .qt-heading-anchor {
        position: absolute;
        left: -28px;
        top: 50%;
        transform: translateY(-50%);
        width: 22px;
        height: 22px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        color: var(--qt-cyan, #00E5FF);
        opacity: 0;
        cursor: pointer;
        padding: 0;
        border-radius: 4px;
        transition: opacity 140ms ease, background 140ms ease;
      }
      .qt-heading-anchor:hover {
        background: rgba(0, 229, 255, 0.10);
      }
      .qt-doc-content h2:hover .qt-heading-anchor,
      .qt-doc-content h3:hover .qt-heading-anchor,
      .qt-legal-prose h2:hover .qt-heading-anchor,
      .qt-legal-prose h3:hover .qt-heading-anchor,
      .qt-heading-anchor:focus-visible {
        opacity: 1;
      }
      @media (max-width: 768px) {
        .qt-heading-anchor {
          /* On mobile, leave the icon visible inline next to the heading
             since hover is unreliable. Render to the right instead. */
          left: auto;
          right: -28px;
          opacity: 0.5;
        }
      }
    `}</style>
  );
}
